import axios from 'axios';

type OpenPdfOptions = {
  blob?: Blob;
  url?: string;
  filename?: string;
};

export type PdfOpenResult = 'opened' | 'downloaded';

export const isMobileDevice = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const ua = (navigator.userAgent || navigator.vendor || (typeof window !== 'undefined' && (window as any).opera) || '').toLowerCase();
  const isTouchDevice = typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1;
  const mobileRegex = /android|iphone|ipad|ipod|iemobile|opera mini|blackberry|mobile|silk/;
  return mobileRegex.test(ua) || isTouchDevice;
};

const triggerDownload = (href: string, filename: string) => {
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Íconos (heroicons outline 24) para los botones del visor: la UI no lleva emojis
const ICONO_IMPRESORA = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0 1 10.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0 .229 2.523a1.125 1.125 0 0 1-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0 0 21 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 0 0-1.913-.247M6.34 18H5.25A2.25 2.25 0 0 1 3 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 0 1 1.913-.247m10.5 0a48.536 48.536 0 0 0-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5Zm-3 0h.008v.008H15V10.5Z"/></svg>';
const ICONO_LOCAL = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M13.5 21v-7.5a.75.75 0 0 1 .75-.75h3a.75.75 0 0 1 .75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349M3.75 21V9.349m0 0a3.001 3.001 0 0 0 3.75-.615A2.993 2.993 0 0 0 9.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 0 0 2.25 1.016c.896 0 1.7-.393 2.25-1.015a3.001 3.001 0 0 0 3.75.614m-16.5 0a3.004 3.004 0 0 1-.621-4.72l1.189-1.19A1.5 1.5 0 0 1 5.378 3h13.243a1.5 1.5 0 0 1 1.06.44l1.19 1.189a3 3 0 0 1-.621 4.72M6.75 18h3.75a.75.75 0 0 0 .75-.75V13.5a.75.75 0 0 0-.75-.75H6.75a.75.75 0 0 0-.75.75v3.75c0 .414.336.75.75.75Z"/></svg>';
const ICONO_DESCARGA = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"/></svg>';
const ICONO_TILDE = '<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/></svg>';
const ICONO_CRUZ = '<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>';

// ───────────── Opciones de impresión en el local (v12.4.9) ─────────────
// Copias, color o blanco y negro, calidad, orientación y doble faz. Se recuerda
// lo último elegido (menos las copias, que vuelven a 1, y el doble faz) en el
// mismo lugar desde el visor de escritorio y desde el celular.

export type OpcionesImpresion = {
  copias: number;
  color: boolean;
  calidad: 'borrador' | 'normal' | 'alta';
  orientacion: 'auto' | 'vertical' | 'horizontal';
  dobleFaz: boolean;
};
const OPCIONES_KEY = 'rocam-impresion-opciones';
const CALIDADES = ['borrador', 'normal', 'alta'];
const ORIENTACIONES = ['auto', 'vertical', 'horizontal'];

const leerOpciones = (): OpcionesImpresion => {
  const base: OpcionesImpresion = { copias: 1, color: true, calidad: 'normal', orientacion: 'auto', dobleFaz: false };
  try {
    const g = JSON.parse(localStorage.getItem(OPCIONES_KEY) || '{}');
    return {
      copias: 1,
      color: typeof g.color === 'boolean' ? g.color : base.color,
      calidad: CALIDADES.includes(g.calidad) ? g.calidad : base.calidad,
      orientacion: ORIENTACIONES.includes(g.orientacion) ? g.orientacion : base.orientacion,
      dobleFaz: false
    };
  } catch {
    return base;
  }
};

const guardarOpciones = (o: OpcionesImpresion) => {
  try { localStorage.setItem(OPCIONES_KEY, JSON.stringify({ color: o.color, calidad: o.calidad, orientacion: o.orientacion })); }
  catch { /* sin storage: no se recuerda */ }
};

// Doble faz (v12.5.4): la Epson del local no da vuelta la hoja sola. Salen los
// frentes, el trabajo queda en 'espera_dorso' y se pide dar vuelta las hojas;
// recién con el OK (POST /api/print-jobs/:id/dorso) sale el dorso.
const PASOS_DORSO_HTML = [
  'Sacá las hojas de la bandeja de salida, todas juntas y sin desordenarlas.',
  'Cargalas de nuevo en la bandeja de atrás: el lado en blanco mirando hacia vos, y el borde que salió primero de la impresora (el que quedó más cerca tuyo) hacia abajo.',
  'Tocá «Imprimir el dorso».'
].map((paso) => `<li>${paso}</li>`).join('');
const NOTA_DORSO = 'Mientras tanto la impresora del local no saca otros trabajos (espera hasta 5 minutos).';

/**
 * Script del visor de escritorio: "Imprimir en el local" abre un panel con las
 * opciones y manda el PDF a la cola (/api/print-jobs); después sigue el trabajo
 * hasta que el programa de la PC del local confirma. El botón solo aparece si la
 * impresora está instalada. El PDF se baja al abrir el visor: así "Descargar" e
 * "Imprimir en el local" siguen andando aunque la URL original se revoque.
 */
const scriptVisor = (src: string, nombre: string, api: string, token: string) => `
(function () {
  var SRC = ${JSON.stringify(src)}, NOMBRE = ${JSON.stringify(nombre)}, API = ${JSON.stringify(api)}, TOKEN0 = ${JSON.stringify(token)};
  var CLAVE = ${JSON.stringify(OPCIONES_KEY)};
  var btn = document.getElementById('local'), panel = document.getElementById('panel');
  var msg = document.getElementById('msg'), dl = document.getElementById('dl'), nCopias = document.getElementById('copias');
  var panelDorso = document.getElementById('panel-dorso'), trabajoDorso = null;
  function tk() { try { return localStorage.getItem('token') || TOKEN0; } catch (e) { return TOKEN0; } }
  function auth() { var t = tk(); return t ? { Authorization: 'Bearer ' + t } : {}; }
  function aviso(t, tipo) { msg.textContent = t; msg.className = 'msg ' + (tipo || ''); }

  var op = { copias: 1, color: true, calidad: 'normal', orientacion: 'auto', dobleFaz: false };
  try {
    var g = JSON.parse(localStorage.getItem(CLAVE) || '{}');
    if (typeof g.color === 'boolean') op.color = g.color;
    if (['borrador', 'normal', 'alta'].indexOf(g.calidad) >= 0) op.calidad = g.calidad;
    if (['auto', 'vertical', 'horizontal'].indexOf(g.orientacion) >= 0) op.orientacion = g.orientacion;
  } catch (e) {}
  function pintar() {
    nCopias.textContent = String(op.copias);
    var grupos = panel.querySelectorAll('[data-grupo]');
    for (var i = 0; i < grupos.length; i++) {
      var grupo = grupos[i].getAttribute('data-grupo');
      var actual = grupo === 'color' ? (op.color ? '1' : '0') : grupo === 'dobleFaz' ? (op.dobleFaz ? '1' : '0') : op[grupo];
      var bs = grupos[i].querySelectorAll('button');
      for (var j = 0; j < bs.length; j++) {
        var on = bs[j].getAttribute('data-valor') === actual;
        bs[j].className = on ? 'on' : '';
        bs[j].setAttribute('aria-pressed', on ? 'true' : 'false');
      }
    }
  }
  panel.addEventListener('click', function (e) {
    var b = e.target.closest ? e.target.closest('button') : null;
    if (!b) return;
    var accion = b.getAttribute('data-accion');
    if (accion === 'menos') op.copias = Math.max(1, op.copias - 1);
    else if (accion === 'mas') op.copias = Math.min(10, op.copias + 1);
    else if (b.parentNode && b.parentNode.getAttribute('data-grupo')) {
      var grupo = b.parentNode.getAttribute('data-grupo'), valor = b.getAttribute('data-valor');
      if (grupo === 'color') op.color = valor === '1'; else if (grupo === 'dobleFaz') op.dobleFaz = valor === '1'; else op[grupo] = valor;
    }
    pintar();
  });
  function abrir() { pintar(); panel.hidden = false; document.getElementById('p-imprimir').focus(); }
  function cerrar() { panel.hidden = true; }
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape') cerrar(); });
  document.addEventListener('mousedown', function (e) { if (!panel.hidden && !panel.contains(e.target) && e.target !== btn && !btn.contains(e.target)) cerrar(); });
  document.getElementById('p-cancelar').onclick = cerrar;

  var pdf = fetch(SRC, { headers: SRC.indexOf('blob:') === 0 ? {} : auth() })
    .then(function (r) { if (!r.ok) throw new Error('No se pudo leer el PDF'); return r.blob(); });
  pdf.then(function (b) { try { dl.href = URL.createObjectURL(b); } catch (e) {} }).catch(function () {});
  fetch(API + '/api/print-jobs/estado', { headers: auth() })
    .then(function (r) { return r.ok ? r.json() : null; })
    .then(function (e) {
      if (!e || !e.configurada) return;
      btn.style.display = '';
      if (!e.agente || !e.agente.conectado) aviso('La PC del local está desconectada: lo que mandes queda en cola', 'warn');
    }).catch(function () {});

  var ocupado = false;
  function fin() { ocupado = false; btn.disabled = false; }
  // max = cuántas consultas (cada 2 s) antes de dejar de esperar; dorso = ya se pidió el dorso
  function seguir(id, n, max, dorso) {
    setTimeout(function () {
      fetch(API + '/api/print-jobs/' + id, { headers: auth() })
        .then(function (r) { return r.json(); })
        .then(function (j) {
          if (j.estado === 'impreso') { aviso(dorso ? 'Listo, salió de los dos lados' : 'Listo, salió en la impresora del local', 'ok'); fin(); }
          else if (j.estado === 'error') { aviso('No se pudo imprimir: ' + (j.error || 'error de la impresora'), 'err'); fin(); }
          else if (j.estado === 'sin_dorso') { aviso('Salieron los frentes y quedó sin el dorso', 'warn'); fin(); }
          else if (j.estado === 'espera_dorso' && !dorso) { trabajoDorso = id; panelDorso.hidden = false; aviso('Falta dar vuelta las hojas', 'warn'); }
          else if (n >= max) {
            aviso(j.estado === 'enviado' ? 'Se mandó a la impresora, todavía sin confirmación' : 'Quedó en cola: sale cuando se prenda la PC del local', 'warn');
            fin();
          } else seguir(id, n + 1, max, dorso);
        })
        .catch(function () { if (n >= max) fin(); else seguir(id, n + 1, max, dorso); });
    }, 2000);
  }
  // Doble faz: ya salieron los frentes. Imprimir el dorso o dejarlo así.
  function resolverDorso(cancelar) {
    var id = trabajoDorso;
    if (!id) return;
    trabajoDorso = null; panelDorso.hidden = true;
    aviso(cancelar ? 'Dejando sin dorso...' : 'Imprimiendo el dorso...');
    var cab = auth(); cab['Content-Type'] = 'application/json';
    fetch(API + '/api/print-jobs/' + id + '/dorso', { method: 'POST', headers: cab, body: JSON.stringify(cancelar ? { cancelar: true } : {}) })
      .then(function (r) { return r.json().then(function (j) { if (!r.ok) { var e = new Error(j.error || 'No se pudo pedir el dorso'); e.definitivo = r.status === 409; throw e; } return j; }); })
      .then(function () {
        if (cancelar) { aviso('Salieron los frentes y quedó sin el dorso', 'warn'); fin(); }
        else seguir(id, 0, 450, true);
      })
      .catch(function (e) {
        aviso((e && e.message) || 'No se pudo pedir el dorso', 'err');
        if (e && e.definitivo) fin(); else { trabajoDorso = id; panelDorso.hidden = false; }   // se puede reintentar
      });
  }
  document.getElementById('d-imprimir').onclick = function () { resolverDorso(false); };
  document.getElementById('d-dejar').onclick = function () { resolverDorso(true); };
  btn.onclick = function () { if (ocupado) return; if (panel.hidden) abrir(); else cerrar(); };
  document.getElementById('p-imprimir').onclick = function () {
    if (ocupado) return;
    cerrar();
    try { localStorage.setItem(CLAVE, JSON.stringify({ color: op.color, calidad: op.calidad, orientacion: op.orientacion })); } catch (e) {}
    var doble = op.dobleFaz;
    ocupado = true; btn.disabled = true; aviso('Enviando a la impresora del local...');
    pdf.then(function (b) {
      var fd = new FormData();
      fd.append('file', b, NOMBRE);
      fd.append('titulo', NOMBRE);
      fd.append('copias', String(op.copias));
      fd.append('color', op.color ? '1' : '0');
      fd.append('calidad', op.calidad);
      fd.append('orientacion', op.orientacion);
      fd.append('dobleFaz', doble ? '1' : '0');
      return fetch(API + '/api/print-jobs', { method: 'POST', headers: auth(), body: fd });
    })
    .then(function (r) { return r.json().then(function (j) { if (!r.ok) throw new Error(j.error || 'No se pudo mandar a la impresora'); return j; }); })
    .then(function (j) {
      op.copias = 1;
      if (!j.agente || !j.agente.conectado) { aviso('Quedó en cola: sale cuando se prenda la PC del local', 'warn'); fin(); return; }
      aviso(doble ? 'Imprimiendo los frentes...' : 'Imprimiendo en el local...');
      // Doble faz: cada cara puede tardar unos minutos
      seguir(j.id, 0, doble ? 450 : 45, false);
    })
    .catch(function (e) { aviso((e && e.message) || 'No se pudo mandar a la impresora', 'err'); fin(); });
  };
})();
`;

// Panel de opciones del visor de escritorio (se completa con scriptVisor)
const panelVisor = () => {
  const seg = (grupo: string, opciones: Array<[string, string]>) =>
    `<div class="seg" data-grupo="${grupo}">${opciones.map(([v, t]) => `<button type="button" data-valor="${v}">${t}</button>`).join('')}</div>`;
  return `
<div id="panel" class="panel" hidden role="dialog" aria-label="Opciones de impresión">
  <div class="p-titulo">Imprimir en el local</div>
  <div class="fila"><span>Copias</span><div class="stepper"><button type="button" data-accion="menos" aria-label="Una copia menos">&minus;</button><span id="copias">1</span><button type="button" data-accion="mas" aria-label="Una copia más">+</button></div></div>
  <div class="fila"><span>Color</span>${seg('color', [['1', 'Color'], ['0', 'Blanco y negro']])}</div>
  <div class="fila"><span>Calidad</span>${seg('calidad', [['borrador', 'Borrador'], ['normal', 'Normal'], ['alta', 'Alta']])}</div>
  <div class="fila"><span>Orientación</span>${seg('orientacion', [['auto', 'Automática'], ['vertical', 'Vertical'], ['horizontal', 'Horizontal']])}</div>
  <div class="fila"><span>Caras</span>${seg('dobleFaz', [['0', 'Una cara'], ['1', 'Doble faz']])}</div>
  <p class="p-nota">Automática respeta cómo viene cada hoja del PDF. Borrador gasta menos tinta. En doble faz salen primero los frentes y te avisa cuándo dar vuelta las hojas.</p>
  <div class="p-acciones"><button type="button" id="p-cancelar" class="sec">Cancelar</button><button type="button" id="p-imprimir" class="local">${ICONO_IMPRESORA} Imprimir</button></div>
</div>
<div id="panel-dorso" class="panel" hidden role="dialog" aria-label="Dar vuelta las hojas">
  <div class="p-titulo">Ya salieron los frentes</div>
  <ol class="pasos">${PASOS_DORSO_HTML}</ol>
  <p class="p-nota">${NOTA_DORSO}</p>
  <div class="p-acciones"><button type="button" id="d-dejar" class="sec">Dejar sin dorso</button><button type="button" id="d-imprimir" class="local">${ICONO_IMPRESORA} Imprimir el dorso</button></div>
</div>`;
};

// Abre el PDF en una nueva pestaña con un viewer que muestra el filename correcto
// y botones para imprimir en el local, imprimir con el cuadro del navegador y
// descargar con ese nombre. Resuelve el problema del UUID cuando el usuario
// quiere guardar desde la pestaña.
const openInBrandedViewer = (resolvedUrl: string, filename: string) => {
  const safeName = filename.replace(/[<>"]/g, '');
  // El visor es un documento blob: las URLs relativas no resuelven ahí adentro
  const src = /^(blob:|https?:)/i.test(resolvedUrl) ? resolvedUrl : new URL(resolvedUrl, window.location.href).href;
  const api = (axios.defaults.baseURL || window.location.origin).replace(/\/$/, '');
  let token = '';
  try { token = localStorage.getItem('token') || ''; } catch { /* sin storage */ }
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${safeName}</title>
<style>
  html,body{margin:0;padding:0;height:100%;background:#525659;font-family:system-ui,-apple-system,sans-serif;color:#fff}
  .bar{position:relative;display:flex;align-items:center;justify-content:space-between;gap:12px;padding:8px 16px;background:#323639;border-bottom:1px solid #1d1f20}
  .bar .name{font-size:14px;font-weight:500;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;min-width:0}
  .bar .actions{display:flex;align-items:center;gap:8px;flex-shrink:0}
  button,a.btn{display:inline-flex;align-items:center;gap:6px;padding:6px 14px;background:#3b82f6;color:#fff;text-decoration:none;border:0;border-radius:6px;font-size:13px;font-weight:500;font-family:inherit;cursor:pointer}
  a.btn:hover,button:hover{background:#2563eb}
  button.print{background:#4b5563}
  button.print:hover{background:#374151}
  button.local{background:#059669}
  button.local:hover{background:#047857}
  button.sec{background:transparent;color:#d1d5db}
  button.sec:hover{background:#3f4448}
  button:disabled{opacity:.6;cursor:default}
  button:focus-visible,a.btn:focus-visible{outline:2px solid #93c5fd;outline-offset:2px}
  .msg{font-size:12px;color:#d1d5db;max-width:420px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  .msg.ok{color:#6ee7b7}.msg.warn{color:#fcd34d}.msg.err{color:#fca5a5}
  .panel{position:absolute;right:16px;top:calc(100% + 6px);z-index:10;width:380px;max-width:calc(100vw - 32px);box-sizing:border-box;background:#323639;border:1px solid #1d1f20;border-radius:10px;padding:14px;box-shadow:0 12px 32px rgba(0,0,0,.45)}
  .panel[hidden]{display:none}
  .p-titulo{font-size:14px;font-weight:600;margin-bottom:12px}
  .fila{display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:10px;font-size:13px;color:#e5e7eb}
  .seg{display:inline-flex;background:#1f2224;border-radius:8px;padding:2px}
  .seg button{background:transparent;color:#cbd5e1;padding:5px 10px;font-size:12px;border-radius:6px}
  .seg button:hover{background:#2b2f32}
  .seg button.on{background:#4b5563;color:#fff}
  .stepper{display:inline-flex;align-items:center;gap:4px;background:#1f2224;border-radius:8px;padding:2px}
  .stepper button{background:transparent;color:#fff;padding:4px 12px;font-size:15px;border-radius:6px}
  .stepper button:hover{background:#2b2f32}
  .stepper span{min-width:22px;text-align:center;font-variant-numeric:tabular-nums;font-size:13px}
  .p-nota{font-size:11px;color:#9ca3af;margin:2px 0 12px}
  .pasos{margin:0 0 10px;padding-left:18px;font-size:13px;line-height:1.45;color:#e5e7eb}
  .pasos li{margin-bottom:6px}
  .p-acciones{display:flex;justify-content:flex-end;gap:8px}
  iframe{width:100%;height:calc(100vh - 45px);border:0;background:#fff}
</style></head><body>
<div class="bar">
  <span class="name">${safeName}</span>
  <div class="actions">
    <span id="msg" class="msg" role="status" aria-live="polite"></span>
    <button id="local" class="local" style="display:none" aria-haspopup="dialog">${ICONO_LOCAL} Imprimir en el local</button>
    <button class="print" onclick="var f=document.getElementById('doc');try{f.contentWindow.focus();f.contentWindow.print();}catch(e){window.print();}">${ICONO_IMPRESORA} Imprimir</button>
    <a id="dl" class="btn" href="${src}" download="${safeName}">${ICONO_DESCARGA} Descargar</a>
  </div>
  ${panelVisor()}
</div>
<iframe id="doc" src="${src}#toolbar=0&navpanes=0"></iframe>
<script>${scriptVisor(src, safeName, api, token)}</script>
</body></html>`;
  const wrapperBlob = new Blob([html], { type: 'text/html' });
  const wrapperUrl = window.URL.createObjectURL(wrapperBlob);
  const w = window.open(wrapperUrl, '_blank');
  setTimeout(() => window.URL.revokeObjectURL(wrapperUrl), 60000);
  return w;
};

// ───────────── Impresora del local desde el celular (v12.4.9) ─────────────
// En el celular los PDF no abren el visor: se muestra una hoja para elegir
// "Imprimir en el local" (con las opciones) o descargar.

type TipoAviso = 'ok' | 'warn' | 'err';
let estadoImpresoraCache: { configurada: boolean; conectado: boolean; at: number } | null = null;

const leerEstadoImpresora = async () => {
  if (estadoImpresoraCache && Date.now() - estadoImpresoraCache.at < 60000) return estadoImpresoraCache;
  try {
    const { data } = await axios.get('/api/print-jobs/estado');
    estadoImpresoraCache = { configurada: Boolean(data?.configurada), conectado: Boolean(data?.agente?.conectado), at: Date.now() };
  } catch {
    estadoImpresoraCache = { configurada: false, conectado: false, at: Date.now() };
  }
  return estadoImpresoraCache;
};

/**
 * Manda un PDF a la impresora del local y sigue el trabajo hasta que sale (o 90 s).
 * Doble faz: cuando salen los frentes llama a `darVuelta`, que avisa y espera a que
 * den vuelta las hojas (false = dejar sin dorso); después manda el dorso.
 */
export const imprimirEnLocal = async (
  blob: Blob,
  nombre: string,
  opciones: OpcionesImpresion,
  avisar: (texto: string, tipo?: TipoAviso) => void,
  darVuelta?: () => Promise<boolean>
) => {
  const fd = new FormData();
  fd.append('file', blob, nombre);
  fd.append('titulo', nombre);
  fd.append('copias', String(opciones.copias));
  fd.append('color', opciones.color ? '1' : '0');
  fd.append('calidad', opciones.calidad);
  fd.append('orientacion', opciones.orientacion);
  fd.append('dobleFaz', opciones.dobleFaz ? '1' : '0');
  avisar('Enviando a la impresora del local...');
  const { data } = await axios.post('/api/print-jobs', fd);
  if (!data?.agente?.conectado) {
    avisar('Quedó en cola: sale cuando se prenda la PC del local', 'warn');
    return;
  }
  avisar(opciones.dobleFaz ? 'Imprimiendo los frentes...' : 'Imprimiendo en el local...');
  // Doble faz: cada cara puede tardar unos minutos y en el medio hay que dar vuelta las hojas
  const vueltas = opciones.dobleFaz ? 450 : 45;
  let dorso = false;
  for (let n = 0; n < vueltas; n++) {
    await new Promise((r) => setTimeout(r, 2000));
    try {
      const { data: job } = await axios.get(`/api/print-jobs/${data.id}`);
      if (job?.estado === 'impreso') { avisar(dorso ? 'Listo, salió de los dos lados' : 'Listo, salió en la impresora del local', 'ok'); return; }
      if (job?.estado === 'error') { avisar(`No se pudo imprimir: ${job.error || 'error de la impresora'}`, 'err'); return; }
      if (job?.estado === 'sin_dorso') { avisar('Salieron los frentes y quedó sin el dorso', 'warn'); return; }
      if (job?.estado === 'espera_dorso' && !dorso) {
        avisar('Falta dar vuelta las hojas', 'warn');
        const seguir = darVuelta ? await darVuelta() : false;
        await axios.post(`/api/print-jobs/${data.id}/dorso`, seguir ? {} : { cancelar: true });
        if (!seguir) { avisar('Salieron los frentes y quedó sin el dorso', 'warn'); return; }
        dorso = true;
        n = 0;
        avisar('Imprimiendo el dorso...');
      }
    } catch { /* se reintenta */ }
  }
  avisar('Se mandó a la impresora, todavía sin confirmación', 'warn');
};

const COLOR_AVISO: Record<TipoAviso | 'info', string> = {
  info: 'text-muted-foreground',
  ok: 'text-emerald-600 dark:text-emerald-400',
  warn: 'text-amber-600 dark:text-amber-400',
  err: 'text-destructive'
};
const SEG_ON = 'bg-background text-foreground shadow-sm';
const SEG_OFF = 'text-muted-foreground';

const segMovil = (grupo: string, opciones: Array<[string, string]>) =>
  `<div data-grupo="${grupo}" class="inline-flex rounded-lg bg-muted p-0.5">${opciones
    .map(([v, t]) => `<button type="button" data-valor="${v}" class="rounded-md px-2.5 py-1.5 text-xs font-medium ${SEG_OFF}">${t}</button>`)
    .join('')}</div>`;

const mostrarOpcionesMovil = ({ blob, url, filename }: { blob?: Blob; url?: string; filename: string }) => {
  document.getElementById('rocam-pdf-opciones')?.remove();
  const op = leerOpciones();
  const overlay = document.createElement('div');
  overlay.id = 'rocam-pdf-opciones';
  overlay.className = 'fixed inset-0 z-[100] flex items-end justify-center bg-black/50';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', 'Opciones del PDF');
  overlay.innerHTML = `
    <div class="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-t-2xl border border-border bg-background p-4 text-foreground shadow-xl" style="padding-bottom:max(1rem, env(safe-area-inset-bottom))">
      <div class="mx-auto mb-3 h-1 w-10 rounded-full bg-muted"></div>
      <p data-id="nombre" class="truncate text-sm font-semibold"></p>
      <p class="mb-3 text-xs text-muted-foreground">¿Qué querés hacer con este PDF?</p>
      <div data-id="opciones" class="mb-3 hidden space-y-2.5 rounded-xl border border-border p-3">
        <div class="flex items-center justify-between gap-2 text-sm"><span>Copias</span>
          <div class="inline-flex items-center gap-1 rounded-lg bg-muted p-0.5">
            <button type="button" data-accion="menos" aria-label="Una copia menos" class="h-8 w-9 rounded-md text-base">&minus;</button>
            <span data-id="copias" class="min-w-[1.5rem] text-center text-sm tabular-nums">1</span>
            <button type="button" data-accion="mas" aria-label="Una copia más" class="h-8 w-9 rounded-md text-base">+</button>
          </div>
        </div>
        <div class="flex items-center justify-between gap-2 text-sm"><span>Color</span>${segMovil('color', [['1', 'Color'], ['0', 'B y N']])}</div>
        <div class="flex items-center justify-between gap-2 text-sm"><span>Calidad</span>${segMovil('calidad', [['borrador', 'Borrador'], ['normal', 'Normal'], ['alta', 'Alta']])}</div>
        <div class="flex items-center justify-between gap-2 text-sm"><span>Orientación</span>${segMovil('orientacion', [['auto', 'Auto'], ['vertical', 'Vertical'], ['horizontal', 'Horiz.']])}</div>
        <div class="flex items-center justify-between gap-2 text-sm"><span>Caras</span>${segMovil('dobleFaz', [['0', 'Una cara'], ['1', 'Doble faz']])}</div>
      </div>
      <div data-id="dorso" class="mb-3 rounded-xl border border-border p-3" style="display:none">
        <p class="text-sm font-semibold">Ya salieron los frentes</p>
        <ol class="mt-2 list-decimal space-y-1.5 pl-5 text-sm">${PASOS_DORSO_HTML}</ol>
        <p class="mt-2 text-xs text-muted-foreground">${NOTA_DORSO}</p>
        <div class="mt-3 flex flex-col gap-2">
          <button data-id="dorso-si" type="button" class="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 text-sm font-semibold text-white active:bg-emerald-700">${ICONO_IMPRESORA} Imprimir el dorso</button>
          <button data-id="dorso-no" type="button" class="h-11 rounded-xl border border-border px-4 text-sm font-medium active:bg-accent">Dejar sin dorso</button>
        </div>
      </div>
      <div data-id="resultado" class="flex-col items-center gap-3 py-8 text-center" style="display:none" role="status" aria-live="polite">
        <div data-id="res-icono" class="flex h-16 w-16 items-center justify-center rounded-full"></div>
        <p data-id="res-texto" class="max-w-xs text-sm font-medium"></p>
        <div data-id="res-botones" class="mt-2 gap-2" style="display:none">
          <button data-id="res-volver" type="button" class="h-11 rounded-xl border border-border px-5 text-sm font-medium active:bg-accent">Volver a intentar</button>
          <button data-id="res-cerrar" type="button" class="h-11 rounded-xl bg-foreground px-5 text-sm font-semibold text-background">Cerrar</button>
        </div>
      </div>
      <div data-id="acciones" class="flex flex-col gap-2">
        <button data-id="local" type="button" disabled class="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 text-sm font-semibold text-white opacity-50 active:bg-emerald-700 disabled:cursor-default">${ICONO_LOCAL} Imprimir en el local</button>
        <p data-id="estado" class="min-h-[1rem] text-center text-xs text-muted-foreground" role="status" aria-live="polite">Buscando la impresora del local...</p>
        <button data-id="descargar" type="button" class="inline-flex h-12 items-center justify-center gap-2 rounded-xl border border-border px-4 text-sm font-medium active:bg-accent">${ICONO_DESCARGA} Descargar</button>
        <button data-id="cancelar" type="button" class="h-11 rounded-xl px-4 text-sm text-muted-foreground active:bg-accent">Cancelar</button>
      </div>
    </div>`;
  document.body.appendChild(overlay);
  const q = <T extends HTMLElement>(id: string) => overlay.querySelector(`[data-id="${id}"]`) as T;
  q('nombre').textContent = filename;
  const btnLocal = q<HTMLButtonElement>('local');
  const estado = q<HTMLParagraphElement>('estado');
  const panelOpciones = q<HTMLDivElement>('opciones');

  const pintar = () => {
    q('copias').textContent = String(op.copias);
    overlay.querySelectorAll<HTMLElement>('[data-grupo]').forEach((g) => {
      const grupo = g.dataset.grupo as 'color' | 'calidad' | 'orientacion' | 'dobleFaz';
      const actual = grupo === 'color' ? (op.color ? '1' : '0') : grupo === 'dobleFaz' ? (op.dobleFaz ? '1' : '0') : op[grupo];
      g.querySelectorAll<HTMLButtonElement>('button').forEach((b) => {
        const on = b.dataset.valor === actual;
        b.className = `rounded-md px-2.5 py-1.5 text-xs font-medium ${on ? SEG_ON : SEG_OFF}`;
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    });
  };
  panelOpciones.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest('button');
    if (!b) return;
    if (b.dataset.accion === 'menos') op.copias = Math.max(1, op.copias - 1);
    else if (b.dataset.accion === 'mas') op.copias = Math.min(10, op.copias + 1);
    else {
      const grupo = (b.parentElement as HTMLElement | null)?.dataset.grupo;
      if (grupo === 'color') op.color = b.dataset.valor === '1';
      else if (grupo === 'calidad') op.calidad = b.dataset.valor as OpcionesImpresion['calidad'];
      else if (grupo === 'orientacion') op.orientacion = b.dataset.valor as OpcionesImpresion['orientacion'];
      else if (grupo === 'dobleFaz') op.dobleFaz = b.dataset.valor === '1';
    }
    pintar();
  });
  pintar();

  // Doble faz: mientras se espera que den vuelta las hojas la hoja no se cierra
  // (hay que elegir "Imprimir el dorso" o "Dejar sin dorso")
  let esperandoDorso = false;
  const cerrar = () => { if (esperandoDorso) return; overlay.remove(); document.removeEventListener('keydown', alTeclado); };
  const alTeclado = (e: KeyboardEvent) => { if (e.key === 'Escape') cerrar(); };
  document.addEventListener('keydown', alTeclado);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) cerrar(); });
  q('cancelar').onclick = cerrar;

  const avisar = (texto: string, tipo?: TipoAviso) => {
    estado.textContent = texto;
    estado.className = `min-h-[1rem] text-center text-xs ${COLOR_AVISO[tipo || 'info']}`;
    if (tipo === 'ok') setTimeout(cerrar, 2500);
  };

  q('descargar').onclick = () => {
    let href = url || '';
    if (blob) {
      try { href = window.URL.createObjectURL(new File([blob], filename, { type: blob.type || 'application/pdf' })); }
      catch { href = window.URL.createObjectURL(blob); }
      setTimeout(() => window.URL.revokeObjectURL(href), 60000);
    }
    if (href) triggerDownload(href, filename);
    cerrar();
  };

  leerEstadoImpresora().then((e) => {
    if (!e.configurada) { btnLocal.remove(); estado.remove(); panelOpciones.remove(); return; }
    panelOpciones.classList.remove('hidden');
    btnLocal.disabled = false;
    btnLocal.classList.remove('opacity-50');
    if (e.conectado) avisar('');
    else avisar('La PC del local está desconectada: lo que mandes queda en cola', 'warn');
  });

  const bloqueDorso = q<HTMLDivElement>('dorso');
  const acciones = q<HTMLDivElement>('acciones');
  const resultado = q<HTMLDivElement>('resultado');
  const resIcono = q<HTMLDivElement>('res-icono');
  const resTexto = q<HTMLParagraphElement>('res-texto');
  const resBotones = q<HTMLDivElement>('res-botones');

  // Qué se ve en la hoja: las opciones, el resultado de la impresión o los pasos del dorso
  const vista = (cual: 'form' | 'resultado' | 'dorso') => {
    panelOpciones.style.display = cual === 'form' ? '' : 'none';
    acciones.style.display = cual === 'form' ? '' : 'none';
    resultado.style.display = cual === 'resultado' ? 'flex' : 'none';
    bloqueDorso.style.display = cual === 'dorso' ? '' : 'none';
  };

  // Mientras imprime: círculo girando. Al terminar: tilde y se cierra sola, o X con el motivo.
  const mostrarResultado = (tipo: 'enviando' | 'ok' | 'aviso' | 'err', texto: string) => {
    if (tipo === 'err' && !overlay.isConnected) { document.body.appendChild(overlay); document.addEventListener('keydown', alTeclado); }
    vista('resultado');
    resTexto.textContent = texto;
    const base = 'flex h-16 w-16 items-center justify-center rounded-full';
    if (tipo === 'enviando') {
      resIcono.className = `${base} bg-muted`;
      resIcono.innerHTML = '<span class="block h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-foreground"></span>';
    } else if (tipo === 'err') {
      resIcono.className = `${base} bg-destructive text-destructive-foreground`;
      resIcono.innerHTML = ICONO_CRUZ;
    } else {
      // "aviso": se mandó pero no está confirmado (quedó en cola, sin dorso, sin respuesta)
      resIcono.className = `${base} ${tipo === 'ok' ? 'bg-emerald-600' : 'bg-amber-500'} text-white`;
      resIcono.innerHTML = ICONO_TILDE;
    }
    resBotones.style.display = tipo === 'err' ? 'flex' : 'none';
    if (tipo === 'ok' || tipo === 'aviso') setTimeout(cerrar, tipo === 'ok' ? 1600 : 3500);
  };
  q<HTMLButtonElement>('res-cerrar').onclick = cerrar;
  q<HTMLButtonElement>('res-volver').onclick = () => vista('form');

  // Doble faz: ya salieron los frentes. Muestra los pasos y espera la decisión.
  const darVuelta = () =>
    new Promise<boolean>((resolve) => {
      // Si habían cerrado la hoja mientras salían los frentes, vuelve a aparecer
      if (!overlay.isConnected) { document.body.appendChild(overlay); document.addEventListener('keydown', alTeclado); }
      esperandoDorso = true;
      vista('dorso');
      const elegir = (seguir: boolean) => () => {
        esperandoDorso = false;
        vista('resultado');
        resolve(seguir);
      };
      q<HTMLButtonElement>('dorso-si').onclick = elegir(true);
      q<HTMLButtonElement>('dorso-no').onclick = elegir(false);
    });

  btnLocal.onclick = async () => {
    btnLocal.disabled = true;
    guardarOpciones(op);
    // imprimirEnLocal avisa el avance; el último aviso dice cómo terminó
    let final: { texto: string; tipo?: TipoAviso } = { texto: '' };
    const seguir = (texto: string, tipo?: TipoAviso) => {
      final = { texto, tipo };
      resTexto.textContent = texto;
    };
    mostrarResultado('enviando', 'Enviando a la impresora del local...');
    try {
      const pdf = blob || (await axios.get(url || '', { responseType: 'blob' })).data;
      await imprimirEnLocal(pdf, filename, { ...op }, seguir, darVuelta);
      if (final.tipo === 'ok') mostrarResultado('ok', final.texto);
      else if (final.tipo === 'err') mostrarResultado('err', final.texto);
      else mostrarResultado('aviso', final.texto || 'Se mandó a la impresora del local');
      op.copias = 1;
      pintar();
    } catch (err: any) {
      mostrarResultado('err', err?.response?.data?.error || 'No se pudo mandar a la impresora');
    } finally {
      btnLocal.disabled = false;
    }
  };
  btnLocal.focus();
};

export const openPdf = ({ blob, url, filename = 'documento.pdf' }: OpenPdfOptions): PdfOpenResult => {
  if (typeof window === 'undefined') {
    return 'downloaded';
  }

  // En el celular: elegir "Imprimir en el local" (con opciones) o descargar
  if (isMobileDevice() && (blob || url)) {
    mostrarOpcionesMovil({ blob, url, filename });
    return 'opened';
  }

  let resolvedUrl: string | null = null;
  let shouldRevoke = false;

  if (blob) {
    // Envolver el Blob en un File para que el navegador use el nombre al guardar
    try {
      const file = new File([blob], filename, { type: blob.type || 'application/pdf' });
      resolvedUrl = window.URL.createObjectURL(file);
    } catch {
      resolvedUrl = window.URL.createObjectURL(blob);
    }
    shouldRevoke = true;
  } else if (url) {
    resolvedUrl = url;
  }

  if (!resolvedUrl) {
    return 'downloaded';
  }

  // En desktop: abrir un viewer custom (HTML wrapper) que muestra el PDF con su nombre
  // y los botones de imprimir en el local, imprimir y descargar
  const opened = openInBrandedViewer(resolvedUrl, filename);
  if (!opened) {
    // Si el navegador bloquea el popup, fallback a descarga directa
    triggerDownload(resolvedUrl, filename);
    if (shouldRevoke) {
      setTimeout(() => window.URL.revokeObjectURL(resolvedUrl!), 60000);
    }
    return 'downloaded';
  }

  if (shouldRevoke) {
    setTimeout(() => window.URL.revokeObjectURL(resolvedUrl!), 60000);
  }
  return 'opened';
};
